import { Image } from "antd";
import { useState } from "react";
import type { TransportTrackMonitorVo } from "@/api/transportTrackMonitorTypes";
import { formatCaptureTime, getCaptureCategoryOption, getCaptureLocation, getCaptureReasons } from "./presentation";

interface CaptureCardProps {
    record: TransportTrackMonitorVo;
    index: number;
    total: number;
    hasPrevious: boolean;
    hasNext: boolean;
    onClose: () => void;
    onPrevious: () => void;
    onNext: () => void;
}

/** 旧版 CaptureCard 的 React 等价组件。 */
export function CaptureCard({
    record,
    index,
    total,
    hasPrevious,
    hasNext,
    onClose,
    onPrevious,
    onNext
}: CaptureCardProps) {
    const [previewOpen, setPreviewOpen] = useState(false);
    const [imageLoadFailed, setImageLoadFailed] = useState(false);
    const category = getCaptureCategoryOption(record);
    const imageUrl = record.captureFile?.path || "";
    const reason = getCaptureReasons(record);
    const detailFields = [
        { label: "抓拍时间", value: formatCaptureTime(record.displayTime || record.captureTime) },
        { label: "运单状态", value: record.orderState?.name || "--" },
        { label: "货仓状态", value: record.warehouseState?.name || "--" },
        { label: "棚架状态", value: record.canopyState?.name || "--" },
        { label: "抓拍原因", value: reason },
        { label: "AIS位置", value: getCaptureLocation(record) }
    ];

    return (
        <article className={`capture-card capture-card--${category.className}`} aria-label="抓拍详情卡片">
            <button
                type="button"
                className="capture-card__image-button"
                disabled={!imageUrl || imageLoadFailed}
                aria-label="查看抓拍原图"
                onClick={() => setPreviewOpen(true)}
            >
                {imageUrl && !imageLoadFailed ? (
                    <img
                        className="capture-card__image"
                        src={imageUrl}
                        alt="抓拍原图"
                        onError={() => setImageLoadFailed(true)}
                    />
                ) : (
                    <span className="capture-card__image-fallback">
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M9 4 7.5 6H5a3 3 0 0 0-3 3v8a3 3 0 0 0 3 3h14a3 3 0 0 0 3-3V9a3 3 0 0 0-3-3h-2.5L15 4H9Zm3 4.5a4.5 4.5 0 1 1 0 9 4.5 4.5 0 0 1 0-9Zm0 2a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5Z" />
                        </svg>
                        图片加载失败
                    </span>
                )}
                <span className="capture-card__image-caption">
                    {category.label}：{reason}
                </span>
                <span className="capture-card__preview" aria-hidden="true">
                    <i className="iconfont icon-eye" />
                    <span>预览</span>
                </span>
            </button>
            <button type="button" className="capture-card__close-button" aria-label="关闭抓拍详情" onClick={onClose}>
                <i className="iconfont icon-close" aria-hidden="true" />
            </button>
            <div className="capture-card__body">
                {detailFields.map(field => (
                    <div key={field.label} className="capture-card__field">
                        <span className="capture-card__label">{field.label}</span>
                        <span className="capture-card__value">{field.value}</span>
                    </div>
                ))}
            </div>
            <footer className="capture-card__footer">
                <div className="capture-card__navigation">
                    <button
                        type="button"
                        className="capture-card__nav-button"
                        disabled={!hasPrevious}
                        onClick={onPrevious}
                    >
                        上一张
                    </button>
                    <span>
                        <em>第{index + 1}张</em> / 共{total}张
                    </span>
                    <button type="button" className="capture-card__nav-button" disabled={!hasNext} onClick={onNext}>
                        下一张
                    </button>
                </div>
            </footer>
            {previewOpen && imageUrl ? (
                <Image
                    className="capture-card__hidden-preview"
                    src={imageUrl}
                    preview={{ visible: previewOpen, onVisibleChange: setPreviewOpen }}
                />
            ) : null}
        </article>
    );
}
